import React, { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';

import { OptionType } from '@chargify/custom-ui-components';

import PaymentProfiles from '../../components/PaymentProfiles';

import {
  useComponentSettings,
  useAvailablePaymentMethods,
  useServices,
  PaymentMethods,
  useSortPaymentProfiles,
} from '../../hooks';
import { paymentProfilesSelectors } from '../../machines';
import { PaymentProfilesContext } from '../../contexts';

export type PaymentProfilesContainerProps = {
  /**
   * Product catalog identifier
   */
  catalogId: number;
  /**
   * Subscription identifier
   */
  subscriptionId: number;
};

const PaymentProfilesContainer = (meta: PaymentProfilesContainerProps) => {
  const { t } = useTranslation('subscriptions-manager');

  const { modalsService } = useServices();
  const navigate = useNavigate();
  const {
    subscription: { details },
  } = useComponentSettings();

  const { paymentProfilesService } = useContext(PaymentProfilesContext);

  const profiles = useSelector(
    paymentProfilesService,
    paymentProfilesSelectors.getPaymentProfiles
  );
  const { isLoading, isError } = useSelector(
    paymentProfilesService,
    paymentProfilesSelectors.getPaymentProfilesState
  );

  const { active, inactive } = useSortPaymentProfiles(profiles);

  const availablePaymentMethods = useAvailablePaymentMethods();

  const handleOptionSelection = (
    option: OptionType,
    paymentProfileId: number
  ) => {
    switch (option.value) {
      case 'select_payment_method':
        return modalsService.send({
          type: 'SHOW_CHANGE_PAYMENT_PROFILE',
          ...meta,
          paymentProfileId,
        });
      case 'view_details':
        return modalsService.send({
          type: 'SHOW_PREVIEW_PAYMENT_PROFILE',
          ...meta,
          paymentProfileId,
        });
      case 'delete_payment_method':
        return modalsService.send({
          type: 'SHOW_DELETE_PAYMENT_PROFILE',
          ...meta,
          paymentProfileId,
        });
      default:
        return console.log('default');
    }
  };

  const options: { label: string; value: PaymentMethods }[] = [
    {
      label: t('payment_profile.credit_card'),
      value: 'credit_card',
    },
  ];

  const paymentMethodsOptions = useMemo(
    () =>
      options.filter(({ value }) => availablePaymentMethods.includes(value)),
    [availablePaymentMethods]
  );
  const handlePaymentProfileCreation = useCallback(
    (option: OptionType) => {
      switch (option.value) {
        case 'credit_card':
          return modalsService.send({
            type: 'SHOW_CREATE_PAYMENT_PROFILE',
            ...meta,
            activePaymentProfileId: active?.id,
            paymentMethod: 'creditCard',
          });
        case 'bank_account':
          return console.log('bank account');
        case 'paypal':
          return console.log('paypal');
        case 'remittance_payment':
          return console.log('remittance payment');
        default:
          return console.log('default');
      }
    },
    [active?.id]
  );

  const goBack = () => {
    if (details === 'page') {
      return navigate(`/subscription/${meta.catalogId}/${meta.subscriptionId}`);
    }
    return navigate(`/`);
  };

  return (
    <PaymentProfiles
      onRetry={() => paymentProfilesService.send({ type: 'RETRY_FETCH' })}
      isError={isError}
      isLoading={isLoading}
      goBack={goBack}
      activePaymentProfile={active}
      paymentProfiles={inactive}
      onSelectPaymentProfileOption={handleOptionSelection}
      onCreatePaymentProfile={handlePaymentProfileCreation}
      availablePaymentMethods={paymentMethodsOptions}
    />
  );
};

export default PaymentProfilesContainer;
